import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { X, Download, Copy, Check } from 'lucide-react';

import { CreateKeyResponse } from '@app/api/api-keys/types';
import { useClipboardPermission } from '@app/hooks/useClipboardPermission';
import { useCopyToClipboard } from '@app/hooks/useCopyToClipboard';

interface ApiKeyCreatedModalProps {
  keyDetails: CreateKeyResponse | null;
  onClose: () => void;
}

export function ApiKeyCreatedModal({ keyDetails, onClose }: ApiKeyCreatedModalProps) {
  const canCopy = useClipboardPermission();
  const { copyToClipboard, isCopied } = useCopyToClipboard();

  if (!keyDetails) return null;

  const handleDownload = () => {
    const data = {
      name: keyDetails.name,
      id: keyDetails.id,
      secret: keyDetails.secret,
      created_at: keyDetails.created_at,
      expires_at: keyDetails.expires_at,
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `jua-api-key-${keyDetails.name.toLowerCase().replace(/\s+/g, '-')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={true} onClose={onClose} aria-labelledby="new-key-title" className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel
          className="mx-auto max-w-lg rounded bg-white dark:bg-gray-800 p-6 w-full"
          role="dialog"
        >
          <div className="flex justify-between items-center mb-4">
            <DialogTitle
              id="new-key-title"
              className="text-lg font-medium text-gray-900 dark:text-gray-100"
            >
              New API Key Created
            </DialogTitle>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400"
              aria-label="Close dialog"
              data-ph-capture-attribute="btn-api-key-created-close"
            >
              <X className="h-5 w-4" aria-hidden="true" />
            </button>
          </div>

          <div className="space-y-4">
            <div
              className="rounded-md bg-yellow-50 dark:bg-yellow-900/30 p-4 space-y-2"
              role="alert"
              aria-live="polite"
            >
              <p className="text-sm text-yellow-700 dark:text-yellow-200 font-medium">
                Make sure to save both your Key ID and Secret now. You won't be able to see the
                Secret again!
              </p>
              <p className="text-sm text-yellow-700 dark:text-yellow-200">
                Note: It may take up to 5 minutes before your new API key becomes active.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label
                  htmlFor="key-id"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Key ID
                </label>
                <div className="mt-1 relative">
                  <input
                    id="key-id"
                    type="text"
                    value={keyDetails.id}
                    readOnly
                    className="block w-full font-mono text-sm rounded-md border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 dark:text-gray-100 pr-10"
                    aria-label="API Key ID"
                    data-ph-capture-attribute="input-api-key-id"
                  />
                  {canCopy && (
                    <button
                      type="button"
                      onClick={() => copyToClipboard(keyDetails.id, 'id')}
                      className="absolute inset-y-0 right-0 flex items-center px-3"
                      aria-label="Copy Key ID to clipboard"
                      data-ph-capture-attribute="btn-copy-key-id"
                    >
                      {isCopied('id') ? (
                        <Check
                          className="h-4 w-4 text-green-600 dark:text-green-500"
                          aria-hidden="true"
                        />
                      ) : (
                        <Copy
                          className="h-4 w-4 text-gray-400 dark:text-gray-500"
                          aria-hidden="true"
                        />
                      )}
                    </button>
                  )}
                </div>
              </div>

              <div>
                <label
                  htmlFor="key-secret"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Secret
                </label>
                <div className="mt-1 relative">
                  <input
                    id="key-secret"
                    type="text"
                    value={keyDetails.secret}
                    readOnly
                    className="block w-full font-mono text-sm rounded-md border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 dark:text-gray-100 pr-10"
                    aria-label="API Key Secret"
                    data-ph-capture-attribute="input-api-key-secret"
                  />
                  {canCopy && (
                    <button
                      type="button"
                      onClick={() => copyToClipboard(keyDetails.secret, 'secret')}
                      className="absolute inset-y-0 right-0 flex items-center px-3"
                      aria-label="Copy Secret to clipboard"
                      data-ph-capture-attribute="btn-api-key-created-secret-copy"
                    >
                      {isCopied('secret') ? (
                        <Check
                          className="h-4 w-4 text-green-600 dark:text-green-500"
                          aria-hidden="true"
                        />
                      ) : (
                        <Copy
                          className="h-4 w-4 text-gray-400 dark:text-gray-500"
                          aria-hidden="true"
                        />
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={handleDownload}
              className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium bg-gray-100 text-gray-900 rounded-md hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              aria-label="Download API key details as JSON file"
              data-ph-capture-attribute="btn-api-key-created-download"
            >
              <Download className="h-4 w-4 mr-2" aria-hidden="true" />
              Download Key Details
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
